/*
 * Liquid product cards for Algolia hits.
 *
 * The Algolia app hands each hit to a template hook that has to return markup synchronously, but
 * the card we want is the theme's own `product-card-standalone`, which only Liquid can render. So
 * the hook returns a placeholder carrying the hit's identity and this module fills it in afterwards
 * with the real card, fetched through the Section Rendering API as
 * `<product-url>?variant=<objectID>&section_id=algolia-product-card`. That section wraps the shared
 * snippet for one reason: an SRA request by file name cannot pass the `search` view context.
 *
 * The `variant` param is the reason the fetched card agrees with the hit: it makes
 * `product.selected_or_first_available_variant` resolve to the variant Algolia matched, so image,
 * price and title are the ones the shopper searched for. Same trick product-card.js already uses
 * for swatch morphing.
 *
 * Two things make this cheap enough to do per hit. Every card URL is stable, so the CDN caches it
 * across visitors; and `cache` below keeps the resolved HTML for the session, which matters more
 * than it looks: InstantSearch is Preact and throws the whole hit list away on every keystroke and
 * every facet refine. `getCard` is deliberately synchronous so the template hook can inject a
 * cached card in the same tick, with no skeleton and no flash, and only genuinely new
 * handle+variant pairs ever hit the network.
 *
 * Loaded as a classic deferred script and exposed on `window`, matching algolia-helpers.js: the two
 * consumer files are not modules, and `defer` keeps them ordered after this one.
 */
(function (window) {
  const SECTION_ID = 'algolia-product-card';

  /* Enough to fill a first screen of results at once without opening 40 sockets on a phone. */
  const MAX_IN_FLIGHT = 6;

  const CARD_SELECTOR = '[data-algolia-card]';

  /* The card's own PDP links, the ones that carry Algolia's click attribution. Deliberately not
     every anchor in the card: `plp-related-color-swatches` links to *other* products, and the
     quick-add CTAs are crawl links that JS intercepts anyway. */
  const OWN_LINK_SELECTOR = 'a.product-card__link, a[ref="productTitleLink"]';

  /**
   * @type {Map<string, string|null>} resolved card HTML, keyed by handle+variant. A `null` entry
   * is a card the section renderer refused, held so the handle is not asked for again.
   */
  const cache = new Map();

  /** @type {Map<string, Promise<string|null>>} in-flight fetches, so the same card is fetched once */
  const pending = new Map();

  /**
   * @type {Array<{key: string, resolve: (value: string|null) => void, run: () => void}>} fetches
   * waiting for a slot. The key travels with the task so the drain can tell whether the page still
   * wants it; `resolve` so one it abandons still settles its caller's promise.
   */
  const queue = [];
  let inFlight = 0;

  /** @type {string|null} */
  let skeletonMarkup = null;

  /**
   * @param {string} handle
   * @param {string} [variantId]
   * @returns {string}
   */
  function cardKey(handle, variantId) {
    return variantId ? `${handle}::${variantId}` : handle;
  }

  /**
   * @param {string} handle
   * @param {string} [variantId]
   * @returns {string}
   */
  function cardUrl(handle, variantId) {
    const url = new URL(`${window.Shopify.routes.root}products/${handle}`, window.location.origin);
    if (variantId) url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', SECTION_ID);
    return url.toString();
  }

  /**
   * The Section Rendering API wraps its output in a `<div id="shopify-section-…">`. Injecting that
   * wrapper inside the hit would add a layout box between the grid cell and the card, so peel it
   * off - and fall back to the whole response if the shape is ever anything else.
   *
   * @param {string} html
   * @returns {string}
   */
  function unwrapSection(html) {
    const holder = document.createElement('template');
    holder.innerHTML = html.trim();

    const children = holder.content.children;
    const wrapper = children.length === 1 ? children[0] : null;

    return wrapper && wrapper.id.startsWith('shopify-section-') ? wrapper.innerHTML : html;
  }

  /**
   * The keys the page is showing a placeholder for right now. `querySelectorAll` answers with
   * connected nodes only, so a panel that has been closed contributes none.
   *
   * @returns {Set<string>}
   */
  function wantedKeys() {
    const keys = new Set();

    for (const element of document.querySelectorAll(CARD_SELECTOR)) {
      const node = /** @type {HTMLElement} */ (element);
      const { handle, variantId } = node.dataset;

      if (handle) keys.add(cardKey(handle, variantId));
    }

    return keys;
  }

  /*
   * Six at a time, and each hit enqueues on every render, so a shopper typing across a full grid
   * builds a backlog of intermediate result sets. Before a task is run it is checked against what
   * the page is actually showing: the cards of the query the shopper has landed on no longer wait
   * behind requests for hits that scrolled out of existence three keystrokes ago.
   */
  function runQueue() {
    /** @type {Set<string>|null} */
    let wanted = null;

    while (inFlight < MAX_IN_FLIGHT && queue.length > 0) {
      const task = queue.shift();
      if (!task) continue;

      // Once per drain rather than per task: `runQueue` runs again as each fetch settles, so the
      // set is never more than one completion out of date.
      wanted ??= wantedKeys();

      if (!wanted.has(task.key)) {
        // Nothing is cached here. The card was never asked for, so a later render that wants it
        // queues it afresh - it is the hit that left, not the card that failed.
        //
        // Only what has not started yet. A request already in flight is still seen through and
        // cached, for the reason the note in `fetchCard` gives: it is paid for either way, and
        // the next render may well want it.
        pending.delete(task.key);
        task.resolve(null);
        continue;
      }

      task.run();
    }
  }

  /**
   * Reads a card response into the cache and resolves with its markup, or `null` for a card the
   * page will not get. Shared by the queued requests and the ones `prefetch` sends past the queue,
   * so both end in the same cache entry.
   *
   * @param {string} key
   * @param {string} url
   * @param {Promise<Response>} request
   * @returns {Promise<string|null>}
   */
  function settleCard(key, url, request) {
    return request
      .then((response) => {
        /*
         * Cached as a failure, so the handle is asked for once. A product still in the index
         * but unpublished on the market being browsed 404s here, and `hydrate` runs on every
         * render of both panels: without this the same doomed request went out on every
         * keystroke and every refinement, for the rest of the session.
         *
         * Only for a response that came back. The `catch` below leaves its key uncached: a
         * lost connection is transient, and poisoning the card for the session over one
         * dropped request would be worse than the retry.
         */
        if (!response.ok) {
          console.warn(`[Algolia] Card fetch failed (${response.status}): ${url}`);
          cache.set(key, null);
          return null;
        }

        return response.text();
      })
      .then((html) => {
        if (!html) return null;

        /*
         * An empty section means the card had no product to draw - see
         * `sections/algolia-product-card.liquid`. Cached as a refusal, exactly like the 404
         * a deleted product gives: both end with the tile leaving the grid, rather than the
         * placeholder card the snippet falls back to.
         */
        const markup = unwrapSection(html).trim() || null;
        cache.set(key, markup);
        return markup;
      })
      .catch(() => null);
  }

  /**
   * @param {string} handle
   * @param {string} [variantId]
   * @returns {Promise<string|null>}
   */
  function fetchCard(handle, variantId) {
    const key = cardKey(handle, variantId);

    // `has`, not the value: a card the renderer refused is cached as `null`, and that entry is
    // what keeps a doomed handle from being fetched again on every pass.
    if (cache.has(key)) return Promise.resolve(cache.get(key) ?? null);

    const inProgress = pending.get(key);
    if (inProgress) return inProgress;

    /*
     * No AbortController here on purpose. Preact detaches and recreates hit nodes constantly, so
     * cancelling on detach would throw away responses that the very next render wants from cache.
     * A card that arrives for a node that is gone is still worth keeping; only the DOM write is
     * skipped, in `renderInto`.
     */
    const promise = new Promise((resolve) => {
      queue.push({
        key,
        resolve,
        run: () => {
          inFlight += 1;

          const url = cardUrl(handle, variantId);

          settleCard(key, url, fetch(url))
            .then(resolve)
            .finally(() => {
              inFlight -= 1;
              pending.delete(key);
              runQueue();
            });
        },
      });
    });

    // Before the queue is drained, not after: a drain can abandon this very task, and the
    // `pending` entry it deletes has to be the one already there.
    pending.set(key, promise);
    runQueue();

    return promise;
  }

  /**
   * Points the card's own PDP links at the hit's attributed URL. Runs on every pass rather than
   * once: when a cached card replaces a skeleton, Preact rewrites the whole subtree, so a "done"
   * flag here would leave the fresh markup with its plain Liquid hrefs.
   *
   * @param {HTMLElement} node
   */
  function applyHitLink(node) {
    const link = node.dataset.algoliaLink;
    if (!link) return;

    node.querySelectorAll(OWN_LINK_SELECTOR).forEach((anchor) => anchor.setAttribute('href', link));
  }

  /**
   * Which card a placeholder is currently holding, as its key - not a boolean. Preact reuses hit
   * nodes positionally, so the node at a given position can be handed a different hit while both
   * the old and the new one are uncached: the vnode's `__html` is the same skeleton and its
   * key attribute is null both times, so Preact writes nothing and the node keeps the previous
   * product's card. Comparing keys catches that, and it also settles the late-arriving fetch: a
   * response for a hit the node no longer shows is dropped instead of overwriting the right card.
   *
   * @param {HTMLElement} node
   * @param {string} key
   * @param {string|null} markup
   */
  function renderInto(node, key, markup) {
    if (!node.isConnected) return;
    if (node.dataset.algoliaCardKey === key) return;
    if (cardKey(node.dataset.handle ?? '', node.dataset.variantId) !== key) return;

    if (!markup) {
      markFailed(node);
      return;
    }

    // Cleared before the write, because the app reuses hit nodes positionally: the one this card
    // is going into may be a tile a failed hit left marked.
    clearFailed(node);
    node.innerHTML = markup;
    node.dataset.algoliaCardKey = key;
    applyHitLink(node);

    // A card written here is one the page did not have a moment ago, and the script tag it carries
    // did not run, so nothing has told `card-hover-slide.js` about its hover slide yet.
    window.CardHoverSlide?.refresh();
  }

  /**
   * Takes the tile out of the grid, rather than leaving a skeleton that loads for the rest of the
   * session. The element that makes the grid cell is the app's own wrapper, so hiding the card
   * alone would leave the cell behind - but it is only marked when this card is all it holds,
   * which a list is not.
   *
   * @param {HTMLElement} node
   */
  function markFailed(node) {
    node.dataset.algoliaCardFailed = '';

    const wrapper = node.parentElement;
    if (wrapper && wrapper.children.length === 1) wrapper.dataset.algoliaCardFailed = '';
  }

  /**
   * @param {HTMLElement} node
   */
  function clearFailed(node) {
    delete node.dataset.algoliaCardFailed;
    delete node.parentElement?.dataset.algoliaCardFailed;
  }

  /**
   * Preloads a card's first image - its first slide, the one on screen - with the same candidates
   * and sizes as the `<img>` the card will insert, so the browser picks the same file and the image
   * finds it already on its way. A template's content is inert, so reading it downloads nothing.
   *
   * @param {string} markup
   */
  function preloadImage(markup) {
    const holder = document.createElement('template');
    holder.innerHTML = markup;

    const image = holder.content.querySelector('img');
    if (!image) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.fetchPriority = 'high';
    if (image.srcset) link.imageSrcset = image.srcset;
    if (image.sizes) link.imageSizes = image.sizes;
    link.href = image.getAttribute('src') ?? '';
    document.head.append(link);
  }

  window.AlgoliaLiquidCard = {
    cardKey,

    /**
     * The card HTML for a key if it is already known, so a template hook can inject it in the same
     * tick instead of showing a skeleton. Synchronous by design.
     *
     * A card the renderer refused reads the same as one not fetched yet: the hook has no use for
     * the difference, it draws the skeleton either way, and `hydrate` takes the tile out of the
     * grid on its next pass.
     *
     * @param {string} key
     * @returns {string|undefined}
     */
    getCard(key) {
      return cache.get(key) ?? undefined;
    },

    /**
     * Starts the first cards of a result set as soon as the hits are known, ahead of the render
     * that will draw them and of the hydration pass behind it: on the results page these are the
     * cards whose first image is the page's largest paint, and every step that waits for the
     * render is time added to it. The first row's images are preloaded from the markup before any
     * pass can insert it, so the image is fetched at high priority rather than by the `<img>`.
     *
     * Outside the queue, because the queue only runs what the page is already showing a placeholder
     * for, and nothing is drawn yet. That is also why it is only meant for a first load: on a
     * keystroke or a refine it would bypass the mobile debounce in `AlgoliaHelpers.hydrateWhenIdle`.
     * A card the render turns out not to ask for stays cached like any other.
     *
     * @param {Array<{handle?: string, objectID?: string}>} hits
     */
    prefetch(hits) {
      // Two rows on desktop, four on mobile: whatever the first screen holds, and a little more.
      const CARDS = 8;
      const images = window.matchMedia('(min-width: 768px)').matches ? 4 : 2;

      hits.slice(0, CARDS).forEach(({ handle, objectID }, position) => {
        if (!handle) return;

        const key = cardKey(handle, objectID);
        if (cache.has(key) || pending.has(key)) return;

        const url = cardUrl(handle, objectID);
        const promise = settleCard(key, url, fetch(url, { priority: position < images ? 'high' : 'auto' })).finally(
          () => pending.delete(key)
        );

        pending.set(key, promise);

        // Chained before anything else can be: a pass waiting on this card gets it in a later
        // callback, so the preload is always in the document first.
        if (position < images) {
          promise.then((markup) => markup && preloadImage(markup));
        }
      });
    },

    /**
     * The placeholder markup, taken from the theme's own skeleton card rendered into a `<template>`
     * next to each Algolia mount point, so there is no second skeleton written in JS.
     *
     * @returns {string}
     */
    getSkeleton() {
      if (skeletonMarkup === null) {
        const template = document.querySelector('#algolia-card-skeleton');
        skeletonMarkup = template ? template.innerHTML.trim() : '';
      }

      return skeletonMarkup;
    },

    /**
     * Fills every placeholder under `root` that is not already holding its own hit's card, and
     * keeps the hit links on the ones that are. Safe to call on every render: cached cards never
     * re-fetch.
     *
     * @param {ParentNode|null|undefined} root
     */
    hydrate(root) {
      if (!root) return;

      root.querySelectorAll(CARD_SELECTOR).forEach((element) => {
        const node = /** @type {HTMLElement} */ (element);
        const { handle, variantId } = node.dataset;
        if (!handle) return;

        const key = cardKey(handle, variantId);

        if (node.dataset.algoliaCardKey === key) {
          applyHitLink(node);
          return;
        }

        // Anything already resolved, a refused card included, is handed over here: going through
        // `fetchCard` for a key known to be dead would allocate a promise per tile per pass.
        if (cache.has(key)) {
          renderInto(node, key, cache.get(key) ?? null);
          return;
        }

        fetchCard(handle, variantId).then((html) => renderInto(node, key, html));
      });

      // The cards `renderInto` never touches: a card already in the module's cache is injected by
      // the template hook itself, in Preact's own render, key attribute and all, so this pass sees
      // it as a tile already holding its hit and writes nothing. Its hover slide is just as new to
      // the page as a freshly fetched one.
      window.CardHoverSlide?.refresh();
    },
  };
})(window);
